/**
 * Utilitaires partagés pour la gestion des indicatifs téléphoniques
 * Gère les territoires ultramarins français (DROM-COM) dont l'indicatif
 * diffère du +33 métropolitain.
 */

export interface CountryCallingCode {
  code: string // ex: "+590"
  label: string // ex: "Guadeloupe (+590)"
  flag: string // ex: "🇬🇵"
  placeholder: string // ex: "0690 12 34 56"
  /** Regex pour valider un numéro local (sans le 0 initial, sans indicatif) */
  localRegex: RegExp
  /** Groupes de chiffres pour l'affichage. Ex: [1,2,2,2,2] pour +33 6 12 34 56 78 */
  formatGroups: number[]
}

/**
 * Liste des indicatifs supportés, dans l'ordre d'affichage dans le sélecteur.
 * La France métropolitaine est en premier (valeur par défaut).
 * IMPORTANT : les codes plus longs doivent apparaître avant les plus courts pour
 * éviter les collisions lors du matching par startsWith.
 */
export const CALLING_CODES: CountryCallingCode[] = [
  {
    code: '+33',
    label: 'France (+33)',
    flag: '🇫🇷',
    placeholder: '06 12 34 56 78',
    localRegex: /^[1-9](\d{2}){4}$/,
    formatGroups: [1, 2, 2, 2, 2],
  },
  {
    code: '+590',
    label: 'Guadeloupe / Saint-Martin (+590)',
    flag: '🇬🇵',
    placeholder: '0690 12 34 56',
    localRegex: /^[67]\d{8}$/,
    formatGroups: [3, 2, 2, 2],
  },
  {
    code: '+596',
    label: 'Martinique (+596)',
    flag: '🇲🇶',
    placeholder: '0696 12 34 56',
    localRegex: /^[67]\d{8}$/,
    formatGroups: [3, 2, 2, 2],
  },
  {
    code: '+594',
    label: 'Guyane (+594)',
    flag: '🇬🇫',
    placeholder: '0694 12 34 56',
    localRegex: /^[67]\d{8}$/,
    formatGroups: [3, 2, 2, 2],
  },
  {
    code: '+262',
    label: 'La Réunion (+262)',
    flag: '🇷🇪',
    placeholder: '0692 12 34 56',
    localRegex: /^[67]\d{8}$/,
    formatGroups: [3, 2, 2, 2],
  },
  {
    code: '+269',
    label: 'Mayotte (+269)',
    flag: '🇾🇹',
    placeholder: '0639 12 34 56',
    localRegex: /^[67]\d{8}$/,
    formatGroups: [3, 2, 2, 2],
  },
  {
    code: '+508',
    label: 'Saint-Pierre-et-Miquelon (+508)',
    flag: '🇵🇲',
    placeholder: '55 12 34',
    localRegex: /^\d{6}$/,
    formatGroups: [2, 2, 2],
  },
  {
    code: '+687',
    label: 'Nouvelle-Calédonie (+687)',
    flag: '🇳🇨',
    placeholder: '75 12 34',
    localRegex: /^\d{6}$/,
    formatGroups: [2, 2, 2],
  },
  {
    code: '+689',
    label: 'Polynésie française (+689)',
    flag: '🇵🇫',
    placeholder: '87 12 34 56',
    localRegex: /^\d{8}$/,
    formatGroups: [2, 2, 2, 2],
  },
  {
    code: '+681',
    label: 'Wallis-et-Futuna (+681)',
    flag: '🇼🇫',
    placeholder: '82 12 34',
    localRegex: /^\d{6}$/,
    formatGroups: [2, 2, 2],
  },
  // --- Pays frontaliers ---
  {
    code: '+32',
    label: 'Belgique (+32)',
    flag: '🇧🇪',
    placeholder: '0470 12 34 56',
    localRegex: /^[1-9]\d{7,8}$/,
    formatGroups: [3, 2, 2, 2],
  },
  {
    code: '+352',
    label: 'Luxembourg (+352)',
    flag: '🇱🇺',
    placeholder: '621 123 456',
    localRegex: /^\d{6,9}$/,
    formatGroups: [3, 3, 3],
  },
  {
    code: '+49',
    label: 'Allemagne (+49)',
    flag: '🇩🇪',
    placeholder: '0151 1234 5678',
    localRegex: /^1[5-7]\d{8,9}$/,
    formatGroups: [3, 4, 4],
  },
  {
    code: '+41',
    label: 'Suisse (+41)',
    flag: '🇨🇭',
    placeholder: '076 123 45 67',
    localRegex: /^[1-9]\d{8}$/,
    formatGroups: [2, 3, 2, 2],
  },
  {
    code: '+39',
    label: 'Italie (+39)',
    flag: '🇮🇹',
    placeholder: '312 345 6789',
    localRegex: /^3\d{8,9}$/,
    formatGroups: [3, 3, 4],
  },
  {
    code: '+34',
    label: 'Espagne (+34)',
    flag: '🇪🇸',
    placeholder: '612 34 56 78',
    localRegex: /^[67]\d{8}$/,
    formatGroups: [3, 2, 2, 2],
  },
  {
    code: '+376',
    label: 'Andorre (+376)',
    flag: '🇦🇩',
    placeholder: '312 345',
    localRegex: /^[367]\d{5}$/,
    formatGroups: [3, 3],
  },
  {
    code: '+377',
    label: 'Monaco (+377)',
    flag: '🇲🇨',
    placeholder: '06 12 34 56 78',
    localRegex: /^[46]\d{7}$/,
    formatGroups: [1, 2, 2, 2, 2],
  },
]

/**
 * Liste triée par longueur décroissante du code pour un matching sans collision
 * (évite que "+5" matche avant "+590", "+596", etc.)
 */
const CALLING_CODES_BY_LENGTH = [...CALLING_CODES].sort(
  (a, b) => b.code.length - a.code.length,
)

/**
 * Map code postal (3 premiers chiffres pour DROM-COM) → indicatif.
 * Seuls les préfixes officiellement attribués par La Poste sont listés.
 * Source : https://www.laposte.fr/professionnel/outils/trouver-un-code-postal
 */
const POSTAL_CODE_TO_CALLING_CODE: Record<string, string> = {
  '971': '+590', // Guadeloupe
  '972': '+596', // Martinique
  '973': '+594', // Guyane
  '974': '+262', // La Réunion
  '975': '+508', // Saint-Pierre-et-Miquelon
  '976': '+269', // Mayotte (indicatif distinct de La Réunion)
  '984': '+687', // Nouvelle-Calédonie
  '986': '+681', // Wallis-et-Futuna
  '987': '+689', // Polynésie française
  '988': '+687', // Nouvelle-Calédonie (arrondissements supplémentaires)
}

/**
 * Déduit l'indicatif téléphonique depuis un code postal français.
 * Retourne +33 pour la métropole ou si le code postal est absent/invalide.
 */
export function getCallingCodeFromPostalCode(postalCode: string | null | undefined): string {
  if (!postalCode) return '+33'
  const cleaned = postalCode.replace(/\s/g, '')
  // Les DROM-COM ont des codes postaux à 5 chiffres commençant par 97x ou 98x
  const prefix3 = cleaned.slice(0, 3)
  return POSTAL_CODE_TO_CALLING_CODE[prefix3] ?? '+33'
}

/**
 * Retrouve la configuration complète d'un indicatif.
 * Retourne France (+33) si non trouvé.
 */
export function getCallingCodeConfig(code: string): CountryCallingCode {
  return CALLING_CODES.find(c => c.code === code) ?? CALLING_CODES[0]!
}

/**
 * Détecte l'indicatif d'un numéro E.164 et retourne {config, localNumber}.
 * Trie par longueur décroissante pour éviter les faux matchs (+5 vs +590).
 */
export function detectCallingCode(e164: string): { config: CountryCallingCode, localNumber: string } | null {
  const match = CALLING_CODES_BY_LENGTH.find(c => e164.startsWith(c.code))
  if (!match) return null
  return { config: match, localNumber: e164.slice(match.code.length) }
}

/**
 * Normalise un numéro de téléphone vers le format international E.164.
 * Ex: "0612345678" + "+33" → "+33612345678"
 *     "0690123456" + "+590" → "+590690123456"
 */
export function normalizePhoneNumber(localNumber: string, callingCode: string): string {
  const digits = localNumber.replace(/\D/g, '')
  // Si le numéro commence par un 0 local, on le retire
  const withoutLeadingZero = digits.startsWith('0') ? digits.slice(1) : digits
  return callingCode + withoutLeadingZero
}

/**
 * Formate un numéro E.164 pour l'affichage selon les groupes définis par territoire.
 * Ex: "+33612345678" → "+33 6 12 34 56 78"
 *     "+590690123456" → "+590 690 12 34 56"
 */
export function formatPhoneForDisplay(e164: string): string {
  if (!e164.startsWith('+')) return e164
  const detected = detectCallingCode(e164)
  if (!detected) return e164
  const { config, localNumber } = detected
  const digits = localNumber.replace(/\D/g, '')
  // Découpe selon les groupes définis
  const groups: string[] = []
  let offset = 0
  for (const size of config.formatGroups) {
    const chunk = digits.slice(offset, offset + size)
    if (chunk) groups.push(chunk)
    offset += size
  }
  // Reste éventuel non couvert par formatGroups
  if (offset < digits.length) groups.push(digits.slice(offset))
  return `${config.code} ${groups.join(' ')}`
}
